/**
 * wallThemes.js ✦ iter212
 * ─────────────────────────────────────────────────────────────
 * Canonical theme registry for Remember Us ✦. Each boothMode
 * resolves to ONE theme. Themes commit to:
 *
 *   • background rendering (surface gradient, dust, vignette)
 *   • material rendering   (stroke variance — chalk vs marker
 *     vs paint vs ivory pen)
 *   • palette suggestions  (the toolbar default ordering)
 *
 * Architectural rules (Craig, iter212):
 *
 *   1. Chalkboard = "remembered classroom", NOT bright school green.
 *      Soft black, dusty warmth, imperfect erasures, low-contrast
 *      edges. Memory of school, not school UI.
 *
 *   2. Brick = "cinematic exterior at golden hour", NOT urban grunge.
 *      Soft shadowing, muted brick, shallow detail, warmth. The
 *      wall HOLDS emotion, never competes with it.
 *
 *   3. Strokes carry per-stroke material variance derived
 *      deterministically from `stroke_id` (or a stable fallback hash
 *      for optimistic local strokes). The SAME stroke renders the
 *      SAME way on every device — projector + drawing + replay.
 *      Mathematical perfection breaks the illusion; deterministic
 *      imperfection makes the wall emotionally convincing.
 *
 *   4. Background ageing is wall_density-driven, NEVER time-driven.
 *      The room changes because PEOPLE PARTICIPATED.
 */

export const BOOTH_MODES = Object.freeze({
  WEDDING_ROOM: 'wedding_room',
  WEDDING_ARCHIVE: 'wedding_archive',
  SCHOOLS_LEAVERS_YEAR6: 'schools_leavers_year6',
  SCHOOLS_LEAVERS_SECONDARY: 'schools_leavers_secondary',
});

const PALETTE_WARM_IVORY = [
  { name: 'ivory', value: '#f8e8c8' },
  { name: 'gold', value: '#c19a4b' },
  { name: 'rose', value: '#e0859b' },
  { name: 'sage', value: '#9bbf9b' },
  { name: 'sky', value: '#8fb8d4' },
  { name: 'charcoal', value: '#2a2a2a' },
];

const PALETTE_CHALK = [
  // Off-whites and dusty pastels — chalk pen colours that REMEMBERED
  // classroom boards actually carried, never neon school cliches.
  { name: 'ivory-chalk', value: '#f5ecd9' },
  { name: 'dusty-rose', value: '#e2b5b5' },
  { name: 'sage-chalk', value: '#bccdb5' },
  { name: 'sky-chalk', value: '#b9cad7' },
  { name: 'butter', value: '#e6d49a' },
  { name: 'lavender', value: '#c8b9d4' },
];

const PALETTE_MARKER = [
  // Paint-marker / chalk-marker hybrid — ivory + gold accents
  // dominate, with warmer secondary highlights.
  { name: 'ivory', value: '#f4ead3' },
  { name: 'gold', value: '#c19a4b' },
  { name: 'rust', value: '#b86a4a' },
  { name: 'wine', value: '#7e3848' },
  { name: 'sage-deep', value: '#5e7a5e' },
  { name: 'charcoal', value: '#1d1612' },
];

export const WALL_THEMES = Object.freeze({
  // ── wedding_room ✦ — the original profile, neutral warm canvas.
  [BOOTH_MODES.WEDDING_ROOM]: Object.freeze({
    id: 'wedding_room',
    material: 'marker',
    palette: PALETTE_WARM_IVORY,
    defaultColor: '#f8e8c8',
    defaultWidth: 3.5,
    surface: {
      // Soft warm radial — what iter210 shipped, preserved for
      // wedding events.
      base: 'radial-gradient(ellipse at center, #2a221b 0%, #15110d 80%)',
      // Vignette + dust opacity grow with wall_density (0..1).
      vignetteFn: (d) => Math.min(0.55 + d * 0.25, 0.85),
      dustOpacityFn: () => 0,
    },
  }),

  // ── wedding_archive ✦ — read-only archive view, slightly more
  //    contemplative than the live room.
  [BOOTH_MODES.WEDDING_ARCHIVE]: Object.freeze({
    id: 'wedding_archive',
    material: 'marker',
    palette: PALETTE_WARM_IVORY,
    defaultColor: '#f8e8c8',
    defaultWidth: 3.5,
    surface: {
      base: 'radial-gradient(ellipse at center, #211a14 0%, #0e0b08 85%)',
      vignetteFn: (d) => Math.min(0.7 + d * 0.15, 0.92),
      dustOpacityFn: () => 0,
    },
  }),

  // ── schools_leavers_year6 ✦ — REMEMBERED chalkboard.
  //    Softer blacks, dusty warmth, imperfect erasures.
  //    NOT bright green classroom board.
  [BOOTH_MODES.SCHOOLS_LEAVERS_YEAR6]: Object.freeze({
    id: 'schools_leavers_year6',
    material: 'chalk',
    palette: PALETTE_CHALK,
    defaultColor: '#f5ecd9',
    defaultWidth: 4.0,
    surface: {
      // Charcoal — "memory of school" tone, never neon green.
      // Slight warmth in the base avoids the "lab board" feel.
      base: 'radial-gradient(ellipse at center, #2a2825 0%, #1a1816 70%, #0f0e0c 100%)',
      // Dust accumulates with density — the board has been
      // "erased" more times as the night progresses.
      vignetteFn: (d) => Math.min(0.45 + d * 0.30, 0.85),
      dustOpacityFn: (d) => Math.min(d * 0.18, 0.18),
    },
  }),

  // ── schools_leavers_secondary ✦ — Cinematic exterior brick.
  //    Soft shadowing, muted brick tones, golden warmth. NOT
  //    urban graffiti alley. The wall holds emotion.
  [BOOTH_MODES.SCHOOLS_LEAVERS_SECONDARY]: Object.freeze({
    id: 'schools_leavers_secondary',
    material: 'marker',
    palette: PALETTE_MARKER,
    defaultColor: '#f4ead3',
    defaultWidth: 3.5,
    surface: {
      // Brick with a golden-hour warmth and a deep top-to-bottom
      // ambient gradient. The brick "texture" is implied with low-
      // frequency layered radials, not a literal repeating pattern.
      base: 'linear-gradient(180deg, #3a261c 0%, #2c1c14 55%, #1a100a 100%)',
      vignetteFn: (d) => Math.min(0.5 + d * 0.25, 0.85),
      dustOpacityFn: (d) => Math.min(d * 0.10, 0.10),
    },
  }),
});

export function resolveTheme(boothMode) {
  return WALL_THEMES[boothMode] || WALL_THEMES[BOOTH_MODES.WEDDING_ROOM];
}

// ── Material variance ────────────────────────────────────────
// A small FNV-1a hash gives every stroke a deterministic "fingerprint"
// derived from its stroke_id (or a stable fallback). Variance is
// always computed from this fingerprint so a given stroke renders
// identically on every device — projector + guest device + replay
// stream all agree on the same imperfection.

function fnvHash32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h;
}

function strokeFingerprint(stroke) {
  const seed = (stroke?.stroke_id || stroke?._localFingerprint || '');
  return fnvHash32(seed || 'fallback');
}

/**
 * materialVariance(stroke, material) → { alpha, widthMul, dust, edgeJitter }
 *
 * Deterministic per stroke. `alpha` is a base opacity in 0.55..1.0;
 * `widthMul` is a 0.9..1.1 scalar applied to the stroke width;
 * `dust` controls a chalk-only secondary pass; `edgeJitter` is a
 * very small per-segment offset that breaks up perfect mathematical
 * lines.
 */
export function materialVariance(stroke, material = 'marker') {
  const h = strokeFingerprint(stroke);
  // Three independent normalised values from the hash.
  const u1 = (h & 0xff) / 255;
  const u2 = ((h >> 8) & 0xff) / 255;
  const u3 = ((h >> 16) & 0xff) / 255;
  switch (material) {
    case 'chalk': {
      // Chalk: opacity varies a lot, slight width variance, a dust
      // companion pass for grain, edge jitter mimics chalk pickup.
      return {
        alpha: 0.72 + u1 * 0.22,
        widthMul: 0.88 + u2 * 0.22,
        dust: 0.18 + u3 * 0.20,
        edgeJitter: 0.5 + u1 * 0.7,
      };
    }
    case 'paint': {
      return {
        alpha: 0.85 + u1 * 0.13,
        widthMul: 0.94 + u2 * 0.14,
        dust: 0,
        edgeJitter: 0.2,
      };
    }
    case 'ivory_pen': {
      return {
        alpha: 0.92 + u1 * 0.08,
        widthMul: 0.96 + u2 * 0.08,
        dust: 0,
        edgeJitter: 0.1,
      };
    }
    case 'marker':
    default: {
      // Marker: slight bloom + occasional dry-streak (a width dip).
      // Edge jitter is tiny — markers paint cleanly compared to chalk.
      const dryStreak = u3 < 0.08 ? 0.78 : 1.0;  // ~8% of strokes
      return {
        alpha: 0.82 + u1 * 0.16,
        widthMul: (0.95 + u2 * 0.12) * dryStreak,
        dust: 0,
        edgeJitter: 0.25,
      };
    }
  }
}

/**
 * Stable assignment of a "subhue" offset per stroke so two strokes
 * of the SAME palette colour still look slightly different — a chalk
 * mark from one guest reads as a different chalk stick than another's.
 * Returns a small ±lightness delta in CSS-friendly format.
 */
export function strokeColor(stroke, baseColor, material = 'marker') {
  const h = strokeFingerprint(stroke);
  const drift = ((h >> 24) & 0xff) / 255;
  // Very small drift — never enough to change the hue family.
  const delta = material === 'chalk' ? (drift - 0.5) * 14 : (drift - 0.5) * 6;
  // Parse #rrggbb and shift each channel by the delta, clamped.
  const m = /^#([0-9a-f]{6})$/i.exec(baseColor || '');
  if (!m) return baseColor;
  const v = parseInt(m[1], 16);
  let r = (v >> 16) & 0xff, g = (v >> 8) & 0xff, b = v & 0xff;
  r = Math.max(0, Math.min(255, Math.round(r + delta)));
  g = Math.max(0, Math.min(255, Math.round(g + delta)));
  b = Math.max(0, Math.min(255, Math.round(b + delta)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export default {
  BOOTH_MODES,
  WALL_THEMES,
  resolveTheme,
  materialVariance,
  strokeColor,
};
